import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ProductSelectDropdown } from "./ProductSelectDropdown";

import { css } from "@emotion/react";
import { StateT } from "../product-comparison-page-component";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";

import { ReactComponent as AddRoundSvg } from "../../../assets/icons/add-round.svg";
import { PCPSelectionCarousel } from "./PCPSelectionCarousel";
import { ExtractSwarovskiPageData, ProductComparisonPageData } from "@swo/storefront";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { Option } from "fp-ts/es6/Option";
import { eqString } from "fp-ts/es6/Eq";
import { CarouselTip } from "./CarouselTip";
import { CSS_MQ } from "../../../styles/breakpoints";

import cn from "classnames";
import { fontColor, FONTS } from "../../../styles/fonts";
import { T } from "../../../components/T";

export const ProductSelectionDropdown: ReactTFC<{
  selectedProducts: StateT["selectedProducts"];
  products: ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["products"];
  selectedProductsUpdated: (product: StateT["selectedProducts"][0], idx: number) => void;
  selectedNewProduct: (product: StateT["selectedProducts"][0]) => void;
  onRemoveRequested: (idx: number) => void;
  selectedCount: number;
  compareLabel: React.ReactNode;
}> = props => {
  const [selectedFamilyName, setSelectedFamilyName] = useState<string | undefined>(undefined);
  const filteredProducts = getProductsWithout(props.products, props.selectedProducts, selectedFamilyName);

  const [editState, setEditState] = useState<{ tag: "initial" } | { tag: "add-new" } | { tag: "change"; idx: number }>({
    tag: "initial",
  });

  const filterValues = useMemo(() => {
    return pipe(
      props.products.map(p => p.family.name),
      array.uniq(eqString),
    );
  }, [props.products]);

  useEffect(() => {
    setEditState({ tag: "initial" });
  }, [props.selectedProducts]);

  const canAddMore = props.selectedProducts.length < 3;

  const onSelectedCallback = useCallback(
    (product: StateT["selectedProducts"][0]) => {
      if (editState.tag === "change") {
        return props.selectedProductsUpdated(product, editState.idx);
      } else {
        return props.selectedNewProduct(product);
      }
    },
    [editState, props.selectedProductsUpdated, props.selectedNewProduct],
  );

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <ContentContainer>
        <div className={`selected-${props.selectedCount}`} css={wrapperStyles}>
          <div className={`selected-${props.selectedCount}`} css={specificGridStyles}>
            {props.selectedProducts.map((selectedProduct, idx) => {
              return (
                <ProductSelectDropdown
                  hasActiveCarousel={editState.tag === "change" && editState.idx === idx}
                  key={selectedProduct.product.key}
                  label={selectedProduct.product.name}
                  onEditRequested={() => {
                    return setEditState(state => {
                      if (state.tag === "change" && state.idx === idx) {
                        return { tag: "initial" };
                      } else {
                        return { tag: "change", idx: idx };
                      }
                    });
                  }}
                  onRemoveRequested={
                    props.selectedProducts.length > 1
                      ? () => {
                          return props.onRemoveRequested(idx);
                        }
                      : undefined
                  }
                />
              );
            })}

            {canAddMore && (
              <CompareModeButton
                selectedProductsCount={props.selectedProducts.length}
                isChangeActive={editState.tag === "change"}
                hasActiveCarousel={editState.tag === "add-new"}
                onClick={() =>
                  setEditState(state => {
                    if (state.tag !== "add-new") {
                      return { tag: "add-new" };
                    } else {
                      return { tag: "initial" };
                    }
                  })
                }
                label={props.compareLabel}
              />
            )}
          </div>
        </div>
      </ContentContainer>

      {(editState.tag === "change" || editState.tag === "add-new") && (
        <PCPSelectionCarousel
          visible={true}
          products={filteredProducts}
          onSelected={onSelectedCallback}
          selectedCount={props.selectedCount}
          filters={filterValues.map(f => ({
            label: f,
            isSelected: selectedFamilyName === f,
            onClick: () => {
              setSelectedFamilyName(selectedFamilies => {
                if (selectedFamilies === f) {
                  return undefined;
                } else {
                  return f;
                }
              });
            },
          }))}
          resetFilters={() => setSelectedFamilyName(undefined)}
        />
      )}
    </div>
  );
};

const CompareModeButton: ReactTFC<{
  selectedProductsCount: number;
  isChangeActive: boolean;
  hasActiveCarousel: boolean;
  onClick: () => void;
  label: React.ReactNode;
}> = props => {
  return (
    <div
      className={cn(props.isChangeActive ? "change-mode-active" : undefined, `selected-${props.selectedProductsCount}`)}
      css={css`
        display: flex;

        ${CSS_MQ.atMostSm} {
          align-items: flex-end;
          justify-content: center;

          &.selected-2,
          &.selected-3 {
            grid-column: 1 / span 2;
            margin-top: 12px;
          }

          &.selected-2.change-mode-active {
            display: none;
          }
        }

        ${CSS_MQ.atLeastMd} {
          position: absolute;
          left: 100%;
          width: 150px;

          height: 100%;
          align-items: flex-end;
        }
      `}
    >
      {props.hasActiveCarousel && <CarouselTip selectedProductsCount={props.selectedProductsCount} />}

      <button onClick={props.onClick}>
        <AddRoundSvg />
        <span
          css={css`
            ${FONTS.SwarovskiFutura300};
            ${fontColor.black};

            font-size: 12.5px;
            letter-spacing: 0.52px;
          `}
        >
          {props.label}
        </span>
      </button>
    </div>
  );
};

export const selectedClassesWidths = css`
  &.selected-1 {
    ${CSS_MQ.atMostSm} {
      max-width: 100%;
    }

    ${CSS_MQ.atLeastMd} {
      max-width: 50%;
    }
  }

  &.selected-2 {
    ${CSS_MQ.atMostSm} {
      max-width: 100%;
    }

    ${CSS_MQ.atLeastMd} {
      max-width: calc(100% * 2 / 3);
    }
  }

  &.selected-3 {
    ${CSS_MQ.atMostSm} {
      width: 125vw;
      padding-right: 20px;
    }

    ${CSS_MQ.atLeastMd} {
      max-width: 100%;
    }
  }
`;

const wrapperStyles = css`
  ${selectedClassesWidths};

  ${CSS_MQ.atLeastMd} {
    margin-left: auto;
    margin-right: auto;
  }
`;

const specificGridStyles = css`
  display: grid;
  position: relative;

  &.selected-1 {
    grid-template-columns: repeat(2, 1fr);
    ${CSS_MQ.atLeastMd} {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  &.selected-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  &.selected-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  grid-column-gap: 20px;
`;

function getProductsWithout(
  products: ExtractSwarovskiPageData<ProductComparisonPageData>["data"]["products"],
  without: StateT["selectedProducts"],
  includeOnlyMembersOfFamilyByName: string | undefined,
): Array<{
  key: string;
  label: string;
  image: Option<{ src: string; alt: string | undefined; sys: { id: string } }>;
  alreadySelected: boolean;
  productRef: StateT["selectedProducts"][0];
}> {
  return products
    .filter(product =>
      includeOnlyMembersOfFamilyByName ? product.family.name === includeOnlyMembersOfFamilyByName : true,
    )
    .map(product => {
      const isSelected = without.some(w => w.product.key === product.product.key);

      return {
        key: product.product.key,
        label: product.product.name,
        image: pipe(
          product.product.image,
          option.fromNullable,
          option.map(img => ({
            src: img.url,
            alt: img.description || img.title || undefined,
            sys: { id: img.sys.id },
          })),
        ),
        alreadySelected: isSelected,
        productRef: product,
      };
    });
}
